const SENSITIVE_QUERY_PARAMS = ['token'];

const URL_PROPERTY_KEYS = new Set([
  'url',
  'href',
  'currenturl',
  'linkurl',
  'menuclickedurl',
  'referrer',
  'search',
]);

export const RESET_PASSWORD_TOKEN_SESSION_KEY = '__td_reset_password_token';

function tryDecodeURIComponent(value: string): string {
  if (!value.includes('%')) {
    return value;
  }

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function stripSensitiveQueryParamsFromUrl(url: string): string {
  const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  for (const key of Array.from(parsed.searchParams.keys())) {
    if (SENSITIVE_QUERY_PARAMS.includes(key.toLowerCase())) {
      parsed.searchParams.delete(key);
    }
  }

  return parsed.toString();
}

export function sanitizeAnalyticsUrl(url: string): string {
  if (!url) {
    return url;
  }

  try {
    return stripSensitiveQueryParamsFromUrl(tryDecodeURIComponent(url));
  } catch {
    return url.replace(/([?&])token=[^&#]*/gi, '$1').replace(/[?&]$/, '');
  }
}

export function sanitizeAnalyticsSearch(search: string): string {
  if (!search) {
    return search;
  }

  try {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    for (const key of Array.from(params.keys())) {
      if (SENSITIVE_QUERY_PARAMS.includes(key.toLowerCase())) {
        params.delete(key);
      }
    }

    const result = params.toString();
    return result ? `?${result}` : '';
  } catch {
    return search;
  }
}

export function sanitizeAnalyticsValue(value: unknown): unknown {
  if (value == null) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(sanitizeAnalyticsValue);
  }

  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      const lowerKey = key.toLowerCase();
      if (lowerKey === 'token') {
        continue;
      }

      if (URL_PROPERTY_KEYS.has(lowerKey) && typeof val === 'string') {
        result[key] = lowerKey === 'search' ? sanitizeAnalyticsSearch(val) : sanitizeAnalyticsUrl(val);
        continue;
      }

      result[key] = sanitizeAnalyticsValue(val);
    }
    return result;
  }

  return value;
}

export function sanitizeAnalyticsReferrer(referrer: unknown): unknown {
  if (typeof referrer === 'string') {
    return sanitizeAnalyticsUrl(referrer);
  }

  return sanitizeAnalyticsValue(referrer);
}

export function sanitizeAnalyticsProperties<T extends Record<string, unknown>>(properties: T): T {
  return sanitizeAnalyticsValue(properties) as T;
}

export function isAnalyticsBlockedPath(pathname = typeof window !== 'undefined' ? window.location.pathname : ''): boolean {
  return pathname.includes('reset-password');
}

export function stripSensitiveParamsFromBrowserUrl(): void {
  if (typeof window === 'undefined' || !isAnalyticsBlockedPath()) {
    return;
  }

  const search = window.location.search;
  if (!search) {
    return;
  }

  const params = new URLSearchParams(search.slice(1));
  let token: string | null = null;
  for (const key of Array.from(params.keys())) {
    if (key.toLowerCase() !== 'token') {
      continue;
    }

    if (!token) {
      token = params.get(key);
    }
    params.delete(key);
  }

  if (!token) {
    return;
  }

  try {
    sessionStorage.setItem(RESET_PASSWORD_TOKEN_SESSION_KEY, token);
  } catch {
    return;
  }

  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
  history.replaceState(history.state, '', nextUrl);
}
